// chatInput.tsx

import React from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactComponent as SendSvg } from "../send.svg";
import CircularProgress from "@mui/material/CircularProgress"; 

interface ChatInputProps {
  input: string;
  setInput: (val: string) => void;
  inputDisabled: boolean;
  sendMessage: (msg: string, backendPayload?: string) => void;
  dropdownOpen: boolean;
  setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  // ✅ Sostituisci clearFirestoreMessages con la nuova prop
  onOpenClearConfirm: () => void;
  isDarkMode: boolean;
  isResponding: boolean; 
  selectedMultiChips?: string[];
  initialMultiChips?: string[];
  setSelectedMultiChips?: React.Dispatch<React.SetStateAction<string[]>>;
}

const ChatInput = React.memo(({
  input,
  setInput,
  inputDisabled,
  sendMessage,
  dropdownOpen,
  setDropdownOpen,
  dropdownRef,
  // ✅ Usa la nuova prop e rimuovi le vecchie
  onOpenClearConfirm,
  isDarkMode,
  isResponding, 
  selectedMultiChips,
  initialMultiChips,
  setSelectedMultiChips,
}: ChatInputProps) => {
  const trySendMessage = () => {
    // Allow sending if there is text OR if there are selected chips OR if we are removing all filters
    const isRemovingAll = selectedMultiChips?.length === 0 && initialMultiChips && initialMultiChips.length > 0;
    if (input.trim() === "" && (!selectedMultiChips || selectedMultiChips.length === 0) && !isRemovingAll) return;
    sendMessage(input);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    trySendMessage();
  };

  const handleInputKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    // Keep Shift+Enter for new lines, but send on Enter.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      trySendMessage();
    }
  };

  const handleClearChat = () => {
    // ✅ Chiama la nuova funzione per aprire il modale
    onOpenClearConfirm();
  };
  // ... il resto del componente rimane invariato
  
  const chipStyles = React.useMemo(() => ({
    fontWeight: 600,
    fontSize: 14,
    height: 28,
    touchAction: "manipulation",
    flexShrink: 0,
    borderColor: isDarkMode ? '#666' : 'rgba(0, 0, 0, 0.23)',
    color: isDarkMode ? 'white' : 'rgba(0, 0, 0, 0.87)',

    
  }), [isDarkMode]);

  const handleIspiramiClick = React.useCallback(() => {
    sendMessage('⛵Ispirami', '@inspiration');
  }, [sendMessage]);

  const handleOfferteClick = React.useCallback(() => {
    sendMessage('💎Offerte Del Mese', '@specialoffers');
  }, [sendMessage]);

  const suggestionChipsElements = React.useMemo(() => {
    const defs = [
      { label: '⛵Ispirami', title: 'Lasciati ispirare per la tua prossima avventura!', onClick: handleIspiramiClick },
      { label: '💎Offerte Del Mese', title: 'Scopri offerte del mese selezionate per te!', onClick: handleOfferteClick },
    ];
    return defs.map(({ label, title, onClick }) => (
      <Tooltip
        key={label}
        title={title}
        arrow
        disableTouchListener
        placement="top"
        PopperProps={{
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
              },
            },
          ],
        }}
      >
        <span
          style={{
            display: 'inline-block',
            pointerEvents: inputDisabled ? 'none' : 'auto',
          }}
        >
          <Chip
            label={label}
            onClick={onClick}
            variant="outlined"
            disabled={inputDisabled || isResponding}
            sx={chipStyles}
          />
        </span>
      </Tooltip>
    ));
  }, [handleIspiramiClick, handleOfferteClick, inputDisabled, chipStyles, isResponding]);

  const paperRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    if (!paperRef.current) return;
    
    const updateHeight = () => {
      if (paperRef.current) {
        const height = paperRef.current.offsetHeight;
        document.documentElement.style.setProperty('--input-area-height', `${height}px`);
      }
    };

    // Initial update
    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });
    
    resizeObserver.observe(paperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Paper
      ref={paperRef}
      component="form"
      onSubmit={handleSendMessage}
      className="input-area"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        p: 2,
        alignItems: "center",
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)',
        boxShadow: isDarkMode ? '0 -2px 10px rgba(0,0,0,0.3)' : '0 -2px 10px rgba(0,0,0,0.05)',
        backdropFilter: "blur(12px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
         '@media (max-width: 375px)': {
            p: { xs: 1, sm: 2 },  // Imposta un padding laterale minore per schermi piccoli
            },
        
      }}
    >
      <div
        className="chat-input-main-row"
        style={{
          display: "flex",
          width: "min(100%, 900px)",
          margin: "0 auto",
          gap: 0,
          alignItems: "center",
        }}
      >
        {/* Utility Dropdown */}
        <div style={{ position: "relative", marginRight: 8 }} ref={dropdownRef}>
          <button
            type="button"
            aria-label="Open utility menu"
            disabled={inputDisabled}
            style={{
              background: "none",
              border: "none",
              padding: 8,
              borderRadius: 8,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "box-shadow 0.2s cubic-bezier(.4,0,.2,1)",
              color: isDarkMode ? 'white' : 'currentColor',
            }}
            onClick={() => setDropdownOpen(prev => !prev)}
            onMouseOver={e => (e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)")}
            onMouseOut={e => (e.currentTarget.style.boxShadow = "none")}
          >
            <svg viewBox="0 0 256 256" width="1.5em" height="1.5em">
              <path fill="currentColor" d="M220 112v96a20 20 0 0 1-20 20H56a20 20 0 0 1-20-20v-96a20 20 0 0 1 20-20h20a12 12 0 0 1 0 24H60v88h136v-88h-16a12 12 0 0 1 0-24h20a20 20 0 0 1 20 20M96.49 72.49L116 53v83a12 12 0 0 0 24 0V53l19.51 19.52a12 12 0 1 0 17-17l-40-40a12 12 0 0 0-17 0l-40 40a12 12 0 1 0 17 17Z"></path>
            </svg>
          </button>
          {dropdownOpen && (
            <ul
              tabIndex={0}
              className="dropdown-content join join-vertical custom-dropdown-menu"
              style={{
                position: "absolute",
                left: 0,
                bottom: "110%",
                marginBottom: 8,
                width: 180,
                padding: 0,
                listStyle: "none",
                background: isDarkMode ? '#444' : 'var(--white)',
                boxShadow: isDarkMode ? '0 4px 15px rgba(0,0,0,0.5)' : 'var(--card-shadow)',
                borderRadius: 16,
                display: "block",
                transition: "opacity 0.2s cubic-bezier(.4,0,.2,1)",
                opacity: 1,
              }}
            >
              <li>
                <button
                  type="button"
                  className="custom-dropdown-item"
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 100,
                    gap: 5,
                    fontWeight: 600,
                    fontSize: 14,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: isDarkMode ? 'white' : 'var(--text-dark)',
                    padding: "12px 0",
                    borderRadius: 14,
                    transition: "background 0.15s cubic-bezier(.4,0,.2,1)",
                  }}
                  onClick={handleClearChat}
                  onMouseOver={e => (e.currentTarget.style.background = isDarkMode ? '#555' : 'var(--bg-color)')}
                  onMouseOut={e => (e.currentTarget.style.background = "none")}
                >
                  <span className="rounded-lg p-1 text-error" style={{ color: "#e53935", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg viewBox="0 0 24 24" width="1.5em" height="1.5em" className="size-5">
                      <path fill="currentColor" fillRule="evenodd" d="M16.5 4.478v.227a49 49 0 0 1 3.878.512a.75.75 0 1 1-.256 1.478l-.209-.035l-1.005 13.07a3 3 0 0 1-2.991 2.77H8.084a3 3 0 0 1-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 0 1-.256-1.478A49 49 0 0 1 7.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a53 53 0 0 1 3.369 0c1.603.051 2.815 1.387 2.815 2.951m-6.136-1.452a51 51 0 0 1 3.273 0C14.39 3.05 15 3.684 15 4.478v.113a50 50 0 0 0-6 0v-.113c0-.794.609-1.428 1.364-1.452m-.355 5.945a.75.75 0 1 0-1.5.058l.347 9a.75.75 0 1 0 1.499-.058zm5.48.058a.75.75 0 1 0-1.498-.058l-.347 9a.75.75 0 0 0 1.5.058z" clipRule="evenodd"></path>
                    </svg>
                  </span>
                  <span style={{ textAlign: "center" }}>Svuota chat</span>
                </button>
              </li>
            </ul>
          )}
        </div>
        {/* End Utility Dropdown */}
        <TextField
          variant="outlined"
          placeholder="Chiedimi qualcosa..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleInputKeyDown}
          disabled={inputDisabled}
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          InputProps={{
            startAdornment: (selectedMultiChips && selectedMultiChips.length > 0) || (selectedMultiChips?.length === 0 && initialMultiChips && initialMultiChips.length > 0) ? (
              <Stack direction="row" spacing={1} sx={{ marginRight: 1, flexWrap: 'wrap', rowGap: 1, maxWidth: '100%', paddingY: 1 }}>
                {selectedMultiChips?.map((chip) => (
                  <Chip
                    key={chip}
                    label={chip}
                    disabled={inputDisabled}
                    onDelete={() => {
                      if (setSelectedMultiChips) {
                        setSelectedMultiChips(prev => prev.filter(c => c !== chip));
                      }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: 'var(--primary)',
                      color: 'var(--white)',
                      opacity: inputDisabled ? 0.5 : 1,
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                        }
                      }
                    }}
                  />
                ))}
                {selectedMultiChips?.length === 0 && initialMultiChips && initialMultiChips.length > 0 && (
                  <Chip
                    label="➖ Rimuovi tutti i filtri"
                    disabled={inputDisabled}
                    onDelete={() => {
                      if (setSelectedMultiChips && initialMultiChips) {
                        setSelectedMultiChips([...initialMultiChips]);
                      }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: '#e53935', // Red color for removal
                      color: 'white',
                      opacity: inputDisabled ? 0.5 : 1,
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                        }
                      }
                    }}
                  />
                )}
              </Stack>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "24px",
              flexWrap: "wrap",
              paddingLeft: ((selectedMultiChips && selectedMultiChips.length > 0) || (selectedMultiChips?.length === 0 && initialMultiChips && initialMultiChips.length > 0)) ? "8px" : undefined,
              // Remove extra default multiline root padding so 1-line height stays compact.
              "&.MuiInputBase-multiline": {
                paddingTop: 0,
                paddingBottom: 0,
              },
              "& fieldset": {
                borderColor: "#098988",
              },
              "&:hover fieldset": {
                borderColor: "#098988",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#098988",
              },

              backgroundColor: isDarkMode ? '#222' : 'white',
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#098988",
            },

            "& .MuiInputBase-input": {
                color: isDarkMode ? 'white' : 'rgba(0, 0, 0, 0.87)',
                padding: '12px 14px',
            },
            "& .MuiInputBase-inputMultiline": {
              lineHeight: 1.4,
              resize: 'none',
            },

            "& .MuiInputBase-input::placeholder": {
                color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                opacity: 1,
            },
            "@media (max-width: 375px)": {
                "& .MuiInputBase-input": {
                  padding: "8px 14px", // Nuovo padding per schermi piccoli
                },
              },
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <IconButton
          type="submit"
          disabled={inputDisabled}
          className={isResponding ? 'send-button-loading' : ''} // Aggiungi la classe condizionale
          sx={{ p: 1.5, color: "#098988",  marginLeft: '5px',     "@media (max-width: 375px)": {
              p: 1.2,
            }, }}
        >
          {isResponding || inputDisabled? (
            <CircularProgress size={24} sx={{ color: "#098988" }} /> // Mostra l'indicatore di caricamento
          ) : (
            <SendSvg style={{ width: 42, height: 42 }} /> // Mostra l'icona normale
          )}
        </IconButton>
      </div>

      {/* Suggestion Chips */}
      <Stack
        className="chat-input-suggestions"
        direction="row"
        spacing={1}
        sx={{ 
          mt: 1, 
          alignSelf: "center", 
          width: "min(100%, 900px)", 
          pl: { xs: "36px", sm: "48px" },
          overflowX: "auto", 
          overflowY: "visible",
          flexWrap: "nowrap",
          justifyContent: "flex-start",
          pb: 0.5,
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {suggestionChipsElements}
      </Stack>

      <Box
        className="chat-input-ai-disclaimer"
        role="note"
        sx={{
          mt: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          width: "min(100%, 900px)",
          color: isDarkMode ? "rgba(255, 255, 255, 0.72)" : "rgba(0, 0, 0, 0.58)",
          textAlign: "center",
        }}
      >
        <InfoOutlinedIcon aria-hidden="true" sx={{ fontSize: { xs: 12, sm: 14 }, flexShrink: 0 }} />
        <Typography
          component="span"
          sx={{
            fontSize: { xs: "9px", sm: "11px" },
            lineHeight: 1.25,
          }}
        >
          Assistente basato su IA. Le risposte possono contenere inesattezze.
        </Typography>
      </Box>
    </Paper>
  );
});

export default ChatInput;
